import type {
  CreateTakeoffSummaryLineItemRequest,
  IssueStatus,
  SpecMetadataData,
  Takeoff,
  TakeoffSummaryLineItem,
  TakeoffIssueItem,
  TakeoffItem,
  TakeoffItemComponent,
  TakeoffWithItems,
  UpdateTakeoffSummaryLineItemRequest,
  UpdateTakeoffRequest,
  UpdateTakeoffItemRequest,
} from '@/app/types/takeoffs';
import { ApiClient, Paginated, PaginationParams } from './client';

/**
 * API client for takeoffs-related endpoints
 */
export class TakeoffsClient extends ApiClient {
  async reviewIssueAssessment(
    projectId: number,
    takeoffId: number,
    issueId: number,
    evaluationJobId: number,
    action: 'acknowledge' | 'retire',
  ) {
    return this._post<TakeoffIssueItem>(
      `projects/${projectId}/takeoffs/${takeoffId}/issue-assessment/`,
      {
        issue_id: issueId,
        evaluation_job_id: evaluationJobId,
        action,
      },
    );
  }

  /**
   * Fetch all takeoffs for the current user
   * @throws {Error} If the API request fails
   */
  async getAll(projectId: number, params?: PaginationParams) {
    return this._get<Paginated<Takeoff>>(`projects/${projectId}/takeoffs/`, params);
  }

  /**
   * Fetch a single takeoff by ID
   * @throws {Error} If the API request fails
   */
  async getById(projectId: number, takeoffId: number) {
    return this._get<TakeoffWithItems>(`projects/${projectId}/takeoffs/${takeoffId}/`);
  }

  /**
   * Create a new takeoff
   * @throws {Error} If the API request fails
   */
  async create(projectId: number, data: { name: string }): Promise<Takeoff> {
    let documentSetId = this.documentSetId;
    if (documentSetId === undefined) {
      const project = await this._get<{ document_set_id: number }>(`projects/${projectId}/`);
      documentSetId = project.document_set_id;
    }
    return this._post<Takeoff>(`projects/${projectId}/takeoffs/`, {
      ...data,
      document_set_id: documentSetId,
    });
  }

  /**
   * Update a takeoff
   */
  async update(projectId: number, takeoffId: number, data: UpdateTakeoffRequest) {
    return this._patch<Takeoff>(`projects/${projectId}/takeoffs/${takeoffId}/`, data);
  }

  /**
   * Delete a takeoff by ID
   * @throws {Error} If the API request fails
   */
  async delete(projectId: number, takeoffId: number) {
    return this._delete(`projects/${projectId}/takeoffs/${takeoffId}/`);
  }

  /**
   * Delete a takeoff item
   * @throws {Error} If the API request fails
   */
  async deleteItem(projectId: number, takeoffId: number, itemId: number) {
    return this._delete(`projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/`);
  }

  /**
   * Edit a takeoff item
   * @throws {Error} If the API request fails
   */
  async editItem(
    projectId: number,
    takeoffId: number,
    itemId: number,
    data: UpdateTakeoffItemRequest,
  ) {
    return this._patch<TakeoffItem>(
      `projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/`,
      data,
    );
  }

  async processTakeoff(projectId: number, takeoffId: number) {
    return this._post<void>(`projects/${projectId}/takeoffs/${takeoffId}/process/`, {
      force_restart: true,
    });
  }

  async evaluateTakeoff(projectId: number, takeoffId: number, issueGroupKey?: string) {
    return this._post<{ job_id: number }>(`projects/${projectId}/takeoffs/${takeoffId}/evaluate/`, {
      ...(issueGroupKey && { issue_group_key: issueGroupKey }),
    });
  }

  /**
   * Start the matching process for a takeoff item
   * @throws {Error} If the API request fails
   */
  async processItem(
    projectId: number,
    takeoffId: number,
    itemId: number,
  ): Promise<{ message: string }> {
    return this._post<{ message: string }>(
      `projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/processing/`,
      { async_processing: false },
    );
  }

  async createItem(
    projectId: number,
    takeoffId: number,
    params: {
      name: string;
      quantity: string;
      labor_hours?: string;
      labor_per_unit?: string;
      price?: string;
      price_per_unit?: string;
      notes?: string;
    },
  ) {
    return this._post<TakeoffItem>(`projects/${projectId}/takeoffs/${takeoffId}/items/`, params);
  }

  async createSummaryLineItem(
    projectId: number,
    takeoffId: number,
    data: CreateTakeoffSummaryLineItemRequest,
  ) {
    return this._post<TakeoffSummaryLineItem>(
      `projects/${projectId}/takeoffs/${takeoffId}/summary-line-items/`,
      data,
    );
  }

  async updateSummaryLineItem(
    projectId: number,
    takeoffId: number,
    summaryLineItemId: number,
    data: UpdateTakeoffSummaryLineItemRequest,
  ) {
    return this._patch<TakeoffSummaryLineItem>(
      `projects/${projectId}/takeoffs/${takeoffId}/summary-line-items/${summaryLineItemId}/`,
      data,
    );
  }

  async deleteSummaryLineItem(projectId: number, takeoffId: number, summaryLineItemId: number) {
    return this._delete(
      `projects/${projectId}/takeoffs/${takeoffId}/summary-line-items/${summaryLineItemId}/`,
    );
  }

  async restoreTakeoffSnapshot(projectId: number, takeoffId: number, snapshotTimestamp: string) {
    return this._post<TakeoffItem[]>(
      `projects/${projectId}/takeoffs/${takeoffId}/history/restore/`,
      {
        timestamp: snapshotTimestamp,
      },
    );
  }

  async previewTakeoffSnapshot(projectId: number, takeoffId: number, snapshotTimestamp: string) {
    return this._get<TakeoffItem[]>(
      `projects/${projectId}/takeoffs/${takeoffId}/history/${snapshotTimestamp}/`,
    );
  }

  async getTakeoffItemHistory(projectId: number, takeoffId: number) {
    return this._get<{
      changes: {
        id: number;
        history_id: number;
        history_date: string;
        history_change_reason: string | null;
        history_type: string;
        history_user_id: number;
        name: string;
        action_type: 'Deleted item' | 'Created item' | 'Updated item';
        details: {
          quantity: number;
          notes: string;
          metadata: Record<string, unknown>;
          takeoff_id: number;
          created_by_id: number;
          created_at: string;
          updated_at: string;
        };
        diff: Record<string, unknown>;
      }[];
    }>(`projects/${projectId}/takeoffs/${takeoffId}/history/`);
  }

  /**
   * Download a takeoff as CSV
   * @throws {Error} If the API request fails
   */
  async download(takeoffId: string): Promise<Blob> {
    const endpoint = `takeoffs/${takeoffId}/download/`;
    const response = await fetch(`/api/${endpoint}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to download takeoff: ${response.statusText}`);
    }

    return response.blob();
  }

  async validate(projectId: number, takeoffId: number) {
    return this._post<{ status: string; message: string; task_id: string; takeoff_id: number }>(
      `projects/${projectId}/takeoffs/${takeoffId}/validate/`,
    );
  }

  /**
   * Execute a specific issue suggestion for a takeoff
   * @throws {Error} If the API request fails
   */
  async executeIssueSuggestion(
    projectId: number,
    takeoffId: number,
    issueId: number,
  ): Promise<{
    status: string;
    message: string;
    takeoff_id: number;
    issue_id: number;
    issue_title: string;
    task_id?: string;
    error?: string;
  }> {
    return this._post<{
      status: string;
      message: string;
      takeoff_id: number;
      issue_id: number;
      issue_title: string;
      task_id?: string;
      error?: string;
    }>(`projects/${projectId}/takeoffs/${takeoffId}/issues/${issueId}/execute/`);
  }

  /**
   * Update issue status (archive/unarchive)
   * @throws {Error} If the API request fails
   */
  async updateIssueStatus(
    projectId: number,
    takeoffId: number,
    issueId: number,
    status: IssueStatus,
  ): Promise<void> {
    return this._patch<void>(`projects/${projectId}/takeoffs/${takeoffId}/issues/${issueId}/`, {
      status,
    });
  }

  /**
   * Update the free-form user note on an issue.
   * @throws {Error} If the API request fails
   */
  async updateIssueNote(
    projectId: number,
    takeoffId: number,
    issueId: number,
    userNote: string,
  ): Promise<void> {
    return this._patch<void>(`projects/${projectId}/takeoffs/${takeoffId}/issues/${issueId}/`, {
      user_note: userNote,
    });
  }

  /**
   * Toggle the RFI flag on an issue.
   * @throws {Error} If the API request fails
   */
  async updateIssueRfi(
    projectId: number,
    takeoffId: number,
    issueId: number,
    isRfi: boolean,
  ): Promise<void> {
    return this._patch<void>(`projects/${projectId}/takeoffs/${takeoffId}/issues/${issueId}/`, {
      is_rfi: isRfi,
    });
  }

  /**
   * Resolve a contradiction issue by selecting one generated option
   * @throws {Error} If the API request fails
   */
  async resolveIssueOption(
    projectId: number,
    takeoffId: number,
    issueId: number,
    optionId: string,
  ): Promise<TakeoffIssueItem> {
    return this._post<TakeoffIssueItem>(
      `projects/${projectId}/takeoffs/${takeoffId}/issues/${issueId}/resolve/`,
      { option_id: optionId },
    );
  }

  /**
   * Convert a takeoff item to an assembly
   * @throws {Error} If the API request fails
   */
  async convertItemToAssembly(
    projectId: number,
    takeoffId: number,
    itemId: number,
  ): Promise<{
    assembly_id: number;
    message: string;
  }> {
    return this._post<{
      assembly_id: number;
      message: string;
    }>(`projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/convert-to-assembly/`);
  }

  /**
   * Get metadata for a takeoff item and its components
   * @throws {Error} If the API request fails
   */
  async getItemMetadata(
    projectId: number,
    takeoffId: number,
    itemId: number,
  ): Promise<{
    id: number;
    metadata: Record<string, unknown>;
    components: Array<{
      id: number;
      metadata: Record<string, unknown>;
    }>;
    processing_context_ids: number[];
    spec_contexts: Array<{
      document_title: string;
      page_number: number;
      content: string;
    }>;
  }> {
    return this._get<{
      id: number;
      metadata: Record<string, unknown>;
      components: Array<{
        id: number;
        metadata: Record<string, unknown>;
      }>;
      processing_context_ids: number[];
      spec_contexts: Array<{
        document_title: string;
        page_number: number;
        content: string;
      }>;
    }>(`projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/metadata/`);
  }

  /**
   * Get spec metadata for a takeoff (admin-only)
   * Returns per-item spec component counts and per-component spec references.
   * @throws {Error} If the API request fails
   */
  async getSpecMetadata(projectId: number, takeoffId: number): Promise<SpecMetadataData> {
    return this._get<SpecMetadataData>(
      `projects/${projectId}/takeoffs/${takeoffId}/spec-metadata/`,
    );
  }

  /**
   * Get components for a specific takeoff item
   * @throws {Error} If the API request fails
   */
  async getItemComponents(
    projectId: number,
    takeoffId: number,
    itemId: number,
  ): Promise<TakeoffItemComponent[]> {
    const response = await this._get<Paginated<TakeoffItemComponent>>(
      `projects/${projectId}/takeoffs/${takeoffId}/items/${itemId}/components/`,
    );
    return response.items;
  }
}
